import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { getCurrentUserServer, loadRawEntityDataByID } from '@adornis/bh-util/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { LogoConfig } from '@adornis/mails/db/mail-template.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import type { Einzelberatung } from '../entities/einzelberatung.js';
import { GlobalMailTemplate } from '../entities/mail-templates.js';
import { InstitutionMailTemplate, MailTemplateVars } from '../entities/mail-templates.js';
import { Institution } from '../entities/institution.js';
import { User } from '../entities/user.js';
import type { Workshop } from '../entities/workshop.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { getLegacyGlobalSetting } from './global-settings.js';
import { buildDeregisterLink, buildNewsletterOptinLink } from '../mails/mail-helpers.js';
import { getMeetingLinkHtml } from '../notification/_helper.js';
import { toBerlinDate } from '../util.js';
import { format } from 'date-fns';
import { getProductByID } from './product.js';

export const getUserMailVars = registerQuery({
  operationName: 'getUserMailVars1',
  type: () => String,
  params: [
    { name: 'userID', type: () => String },
    { name: 'participationID', type: () => String },
  ],
  resolve: (userID: string, participationID?: string) => async () => {
    const currentUser = await getCurrentUserServer();
    if (!currentUser) throw new Error('not logged in');
    if (!currentUser.hasPermission(Permission.SEND_INSTITUTION_MAILS)) throw new Error('not authorized');

    const user = await loadRawEntityDataByID(User, userID, { _id: 1, title: 1, firstName: 1, lastName: 1, email: 1 });
    if (!user) throw new Error('user not found');

    const token = await generateLoginToken({
      userID: user._id,
      userClass: User._class,
      ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
    });

    const vars: Record<string, string> = {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user.firstName,
      [MailTemplateVars.LAST_NAME]: user.lastName,
      [MailTemplateVars.EMAIL]: user.email,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    if (participationID) {
      vars[MailTemplateVars.DEREGISTER_LINK] = buildDeregisterLink(token, participationID, 'de');
      vars[MailTemplateVars.DEREGISTER_LINK_EN] = buildDeregisterLink(token, participationID, 'en');
    }

    vars[MailTemplateVars.NEWSLETTER_OPTIN_LINK] = buildNewsletterOptinLink(token, 'de');
    vars[MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN] = buildNewsletterOptinLink(token, 'en');

    return JSON.stringify(vars);
  },
});

export const getProductMailVars = registerQuery({
  operationName: 'getProductMailVars',
  type: () => String,
  params: [{ name: 'productID', type: () => String }],
  resolve: (productID: string) => async () => {
    const currentUser = await getCurrentUserServer();
    if (!currentUser) throw new Error('not logged in');

    const product = await runInContext(serverContext.sessionID, () => getProductByID(productID)({
      _id: 1,
      name: 1,
      _class: 1,
      institutionID: 1,
      appointments: {
        _id: 1,
        start: 1,
        end: 1,
        location: 1,
        name: 1,
        meetingLink: { link: 1, code: 1, password: 1 },
      },
      slotConfig: { start: 1, end: 1, slotDuration: 1 },
      settings: {
        _id: 1,
        evaluationLink: 1,
        inheritedEvaluationLink: 1,
        evaluationLinkCode: 1,
        inheritedEvaluationLinkCode: 1,
        courseLocation: 1,
        inheritedCourseLocation: 1,
        blockAutomaticMoveUpDaysInAdvance: 1,
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
        blockSignOffDaysInAdvance: 1,
        inheritedBlockSignOffDaysInAdvance: 1,
      },
    }));

    if (!product) throw new ExpectedError('product not found');
    if (!currentUser.hasPermission(Permission.SEND_INSTITUTION_MAILS, product.institutionID))
      throw new Error('not authorized');

    const vars: Record<string, string> = {};

    if (product.name) {
      vars[MailTemplateVars.PRODUCT_NAME] = product.name;
      vars[MailTemplateVars.EVENT_TITLE] = product.name;
      vars[MailTemplateVars.WORKSHOP_NAME] = product.name;
    }

    try {
      vars[MailTemplateVars.PRODUCT_CARD] = InstitutionMailTemplate.mapProductVar(product as any);
      vars[MailTemplateVars.PRODUCT_CARD_EN] = InstitutionMailTemplate.mapProductVar(product as any, 'en');
    } catch {
      // start date might not be set — skip product card
    }

    if (product._class === 'Workshop') {
      const workshop = product as unknown as Workshop;
      const appointmentWithLink = workshop.appointments?.find(a => a?.meetingLink?.link);
      if (appointmentWithLink) {
        vars[MailTemplateVars.MEETING_LINK] = getMeetingLinkHtml(appointmentWithLink);
        vars[MailTemplateVars.MEETING_CODE] = appointmentWithLink.meetingLink?.code ?? '';
        vars[MailTemplateVars.MEETING_PASSWORD] = appointmentWithLink.meetingLink?.password ?? '';
      }

      const terminString = InstitutionMailTemplate.workshopTerminString(workshop);
      if (terminString && terminString !== '-') {
        vars[MailTemplateVars.EVENT_DATES] = terminString;
        vars[MailTemplateVars.WORKSHOP_TERMINE] = terminString;
      }

      const locations = workshop.appointments?.map(a => a.location).filter(Boolean);
      if (locations?.length) {
        vars[MailTemplateVars.WORKSHOP_LOCATION] = locations.join(', ');
      }
    }

    if (product._class === 'Einzelberatung') {
      const einzelberatung = product as unknown as Einzelberatung;
      const slotConfig = einzelberatung.slotConfig;
      if (slotConfig?.start) {
        vars[MailTemplateVars.EVENT_DATES] = format(toBerlinDate(slotConfig.start), 'dd.MM.yyyy');
      }
    }

    const settings = product.settings;
    if (settings) {
      const evalLink = settings.evaluationLink ?? settings.inheritedEvaluationLink?.[1];
      if (evalLink) vars[MailTemplateVars.EVALUATION_LINK] = evalLink;

      const evalCode = settings.evaluationLinkCode ?? settings.inheritedEvaluationLinkCode?.[1];
      if (evalCode) vars[MailTemplateVars.EVALUATION_CODE] = evalCode;

      const courseLocation = settings.courseLocation ?? settings.inheritedCourseLocation?.[1];
      if (courseLocation) vars[MailTemplateVars.EVENT_LOCATION] = courseLocation;

      const moveUpDays =
        settings.blockAutomaticMoveUpDaysInAdvance ?? settings.inheritedBlockAutomaticMoveUpDaysInAdvance?.[1];
      if (moveUpDays != null) vars[MailTemplateVars.AUTOMATIC_MOVE_UP_DAYS] = String(moveUpDays);

      const signOffDays = settings.blockSignOffDaysInAdvance ?? settings.inheritedBlockSignOffDaysInAdvance?.[1];
      if (signOffDays != null) vars[MailTemplateVars.BLOCK_SIGNOFF_DAYS] = String(signOffDays);
    }

    return JSON.stringify(vars);
  },
});

export const syncInstitutionBannerToTemplates = registerMutation({
  operationName: 'syncInstitutionBannerToTemplates',
  type: () => Boolean,
  params: [{ name: 'institutionID', type: () => ID }],
  resolve: (institutionID: string) => async () => {
    const currentUser = await getCurrentUserServer();
    if (!currentUser?.hasPermission(Permission.MANAGE_INSTITUTION)) throw new ExpectedError('not authorized');

    const institution = await Institution._getInstitutionByID(institutionID);
    if (!institution) throw new ExpectedError('Institution not found');

    const col = mongo().collection(InstitutionMailTemplate._collectionName);
    const filter = { _class: InstitutionMailTemplate._class, institutionID };

    if (institution.emailBannerImageID) {
      const logoConfig = new LogoConfig({
        src: AdornisFile.getServeLink(institution.emailBannerImageID),
        alt: 'Logo',
        width: 250,
        padding: 0,
      }).toObject();
      await col.updateMany(filter, { $set: { logoConfig } });
    } else {
      await col.updateMany(filter, { $unset: { logoConfig: '' } });
    }

    return true;
  },
});

export const syncGlobalBannerToTemplates = registerMutation({
  operationName: 'syncGlobalBannerToTemplates',
  type: () => Boolean,
  resolve: () => async () => {
    const currentUser = await getCurrentUserServer();
    if (!currentUser?.hasPermission(Permission.MANAGE_GLOBAL_MAIL_TEMPLATES)) throw new ExpectedError('not authorized');

    const globalSettings = await getLegacyGlobalSetting()({ emailBannerImageID: 1 });
    const col = mongo().collection(GlobalMailTemplate._collectionName);
    const filter = { _class: GlobalMailTemplate._class };

    if (globalSettings?.emailBannerImageID) {
      const logoConfig = new LogoConfig({
        src: AdornisFile.getServeLink(globalSettings.emailBannerImageID),
        alt: 'Logo',
        width: 250,
        padding: 0,
      }).toObject();
      await col.updateMany(filter, { $set: { logoConfig } });
    } else {
      await col.updateMany(filter, { $unset: { logoConfig: '' } });
    }

    return true;
  },
});
